// Notification Settings Web Component
import { renderPageTitle } from '../utils/emoji-badge.js';

class NotificationSettings extends HTMLElement {
  constructor() {
    super();
    this.settings = null;
    this.deliveryPreview = null;
    this.isLoading = true;
    this.isSaving = false;
    this.currentUser = null;
  }

  connectedCallback() {
    this.render();
    this.loadSettings().then(() => {
      this.attachEventListeners();
    });
  }

  async loadSettings() {
    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      const authData = await window.API.request('/auth/status');
      if (!authData.authenticated) {
        throw new Error('User not authenticated');
      }

      this.currentUser = authData.user;
      const [settingsData, feedFiltersData] = await Promise.all([
        window.API.request(`/users/settings/${this.currentUser.id}`),
        window.API.feedFilters.get(this.currentUser.id)
      ]);
      this.settings = settingsData;
      this.deliveryPreview = feedFiltersData?.deliveryPreview || null;
    } catch (error) {
      window.logger.error('Failed to load notification settings:', error);
      this.showError('Failed to load notification settings');
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div class="notification-settings">
          <div class="loading-state">
            <div class="spinner"></div>
            <div>Loading notification settings...</div>
          </div>
        </div>
      `;
      return;
    }

    this.innerHTML = `
      <div class="notification-settings">
        <div class="settings-header">
          ${renderPageTitle('🔔', 'Notification Settings')}
          <p>Choose how you want to be notified about different activities</p>
        </div>

        <div class="settings-content">
          ${this.renderNotificationGroup('Friend Requests', 'friend_request', 'When someone sends you a friend request')}
          ${this.renderNotificationGroup('Friend Accepts', 'friend_accept', 'When someone accepts your friend request')}
          ${this.renderNotificationGroup('New Posts from Friends', 'new_post', 'When a friend creates a new post', 'new_post')}
          ${this.renderNotificationGroup('Comments on My Posts', 'new_comment', 'When someone comments on your posts')}
          ${this.renderNotificationGroup('Replies to My Comments', 'new_reply', 'When someone replies to your comments')}
          ${this.renderNotificationGroup('New Posts in Groups', 'group_post', 'When someone posts in a group you belong to', 'group_post')}
          ${this.renderNotificationGroup('Comments on My Group Posts', 'group_comment', 'When someone comments on your posts in a group')}
          ${this.renderNotificationGroup('Replies to My Group Comments', 'group_reply', 'When someone replies to your comments in a group')}
        </div>

        <p class="feed-filter-note">Feed filters on your home feed also affect new-post emails. Comment notifications are not filtered yet.</p>

        <div class="settings-footer">
          <div class="save-status" id="save-status"></div>
          <button class="btn btn-secondary" id="returnToSettingsBtn">
            ← Return to Settings
          </button>
        </div>
      </div>
    `;
  }

  renderDeliveryPreview(previewKey) {
    const preview = this.deliveryPreview?.[previewKey];
    if (!preview || !preview.emailEnabled) {
      return '';
    }

    const categories = preview.categories || [];
    if (categories.length === 0) {
      return '';
    }

    return `
      <div class="delivery-preview">
        <p class="delivery-preview-title">What you&apos;ll receive:</p>
        <ul class="delivery-preview-list">
          ${categories.map((cat) => `
            <li class="delivery-preview-item ${cat.willDeliver ? 'will-deliver' : 'blocked'}">
              <span class="delivery-preview-icon">${cat.willDeliver ? '✓' : '✗'}</span>
              <span class="delivery-preview-label">${cat.label}</span>
              ${!cat.willDeliver && cat.blockedBy === 'feed_filter'
    ? '<span class="delivery-preview-reason">Hidden by feed filter</span>'
    : ''}
            </li>
          `).join('')}
        </ul>
        <a href="/" class="delivery-preview-link" data-route="/">Edit feed filters on home feed →</a>
      </div>
    `;
  }

  renderNotificationGroup(title, type, description, previewKey = null) {
    const emailEnabled = this.settings ? this.settings[`${type}_email`] : false;
    const smsEnabled = this.settings ? this.settings[`${type}_sms`] : false;

    return `
      <div class="notification-group">
        <div class="group-header">
          <h3>${title}</h3>
          <p class="group-description">${description}</p>
        </div>
        
        <div class="notification-options">
          <div class="option-row">
            <div class="option-info">
              <span class="option-label">📧 Email Notifications</span>
              <span class="option-description">Receive notifications via email</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" 
                     data-type="${type}_email" 
                     ${emailEnabled ? 'checked' : ''}
                     ${this.isSaving ? 'disabled' : ''}>
              <span class="toggle-slider"></span>
            </label>
          </div>

          <!--div class="option-row">
            <div class="option-info">
              <span class="option-label">📱 SMS Notifications</span>
              <span class="option-description">Receive notifications via text message</span>
            </div>
            <label class="toggle-switch">
              <input type="checkbox" 
                     data-type="${type}_sms" 
                     ${smsEnabled ? 'checked' : ''}
                     ${this.isSaving ? 'disabled' : ''}>
              <span class="toggle-slider"></span>
            </label>
          </div-->
        </div>
        ${previewKey ? this.renderDeliveryPreview(previewKey) : ''}
      </div>
    `;
  }

  attachEventListeners() {
    this.addEventListener('change', async (e) => {
      if (e.target.type === 'checkbox' && e.target.dataset.type) {
        await this.handleSettingChange(e.target);
      }
    });

    const returnBtn = this.querySelector('#returnToSettingsBtn');
    if (returnBtn) {
      returnBtn.addEventListener('click', () => {
        if (window.router) {
          window.router.navigate('/settings');
        }
      });
    }

    this.querySelectorAll('.delivery-preview-link[data-route]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        if (window.router) {
          window.router.navigate(link.dataset.route);
        }
      });
    });
  }

  async handleSettingChange(checkbox) {
    if (this.isSaving) return;

    const settingType = checkbox.dataset.type;
    const isEnabled = checkbox.checked;

    try {
      this.isSaving = true;
      this.updateSaveStatus('Saving...', 'saving');

      if (this.settings) {
        this.settings[settingType] = isEnabled;
      }

      const updateData = { [settingType]: isEnabled };
      await window.API.request(`/users/settings/${this.currentUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      const feedFiltersData = await window.API.feedFilters.get(this.currentUser.id);
      this.deliveryPreview = feedFiltersData?.deliveryPreview || null;

      this.render();
      this.attachEventListeners();

      this.updateSaveStatus('Settings saved!', 'success');

      setTimeout(() => {
        this.updateSaveStatus('', '');
      }, 2000);

    } catch (error) {
      window.logger.error('Failed to update notification setting:', error);

      checkbox.checked = !isEnabled;
      if (this.settings) {
        this.settings[settingType] = !isEnabled;
      }

      this.updateSaveStatus('Failed to save settings', 'error');

      setTimeout(() => {
        this.updateSaveStatus('', '');
      }, 3000);
    } finally {
      this.isSaving = false;
    }
  }

  updateSaveStatus(message, type) {
    const statusElement = this.querySelector('#save-status');
    if (statusElement) {
      statusElement.textContent = message;
      statusElement.className = `save-status ${type}`;
    }
  }

  showError(message) {
    this.innerHTML = `
      <div class="notification-settings">
        <div class="error-state">
          <div class="error-icon">⚠️</div>
          <h3>Error Loading Settings</h3>
          <p>${message}</p>
          <button class="retry-button" onclick="this.parentElement.parentElement.loadSettings()">
            Try Again
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('notification-settings', NotificationSettings);
